// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import { Text } from '../Text.js';
import Latex from '../Latex.js';
import { List } from '../List.js';
import Image from '../Image.js';
import DraftImage from '../DraftImage.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import TabularData from '../TabularData.js';
import { TwoColumnProof } from '../TwoColumnProof.js';
import { AlgebraicWorking } from '../AlgebraicWorking.js';
import { EngageNoteCollapsibleInternal } from './EngageNoteCollapsibleInternal.js';
import { productColor } from '../designSystem.js';
import { SubHeading } from '../Heading.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

export function EANHookSection(props: {|
  routineName: string,
  timeEstimate: number,
  children: [Element<typeof EANHookDescription>, Element<typeof EANHookPanel>],
|}) {
  const { failedValidation } = usePropsValidation(props, EANHookSection);
  if (failedValidation) return null;
  return (
    <div style={{ color: productColor.shuttleGrey }}>
      <div style={{ marginTop: 16 }}>
        <SubHeading>Hook</SubHeading>
        <div style={{ color: productColor.shuttleGrey }}>
          {props.timeEstimate} mins
          <svg width="16" height="16" viewBox="0 0 8 16" fill="none">
            <circle cx="4" cy="11" r="2" fill={productColor.shuttleGrey} />
          </svg>
          {props.routineName}
        </div>
      </div>
      <div style={{ marginTop: 16 }}>{props.children}</div>
    </div>
  );
}
EANHookSection.spec = {
  routineName: t.string,
  timeEstimate: t.number,
  children: t.exactOrderChildrenArray(
    t.elementType(EANHookDescription),
    t.elementType(EANHookPanel),
  ),
};

export function EANHookDescription(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANHookDescription);
  if (failedValidation) return null;
  return <div>{props.children}</div>;
}
EANHookDescription.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

export function EANHookPanel(props: {|
  children: ChildrenArray<
    | Element<typeof Text>
    | Element<typeof Latex>
    | Element<typeof LatexTextAtom>
    | Element<typeof List>
    | Element<typeof Image>
    | Element<typeof DraftImage>
    // probably only need those above this line
    | Element<typeof BoxAndWhiskerPlot>
    | Element<typeof CartesianPlane>
    | Element<typeof NumberLine>
    | Element<typeof StemAndLeafPlot>
    | Element<typeof TabularData>
    | Element<typeof TwoColumnProof>
    | Element<typeof AlgebraicWorking>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANHookPanel);
  if (failedValidation) return null;
  return (
    <EngageNoteCollapsibleInternal title="Implementation details">
      {props.children}
    </EngageNoteCollapsibleInternal>
  );
}
EANHookPanel.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      // Primitives
      t.elementType(Text),
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(List),
      t.elementType(Image),
      t.elementType(DraftImage),
      // probably only need those above this line
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(NumberLine),
      t.elementType(StemAndLeafPlot),
      t.elementType(TabularData),
      t.elementType(TwoColumnProof),
      t.elementType(AlgebraicWorking),
    ),
  ),
};
